import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraTag } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FileUploader } from '../../../components/ProjectList/FileUploader';
import { useUploadArtifacts } from '../../../hooks/useDeliveryDocuments';
import { useUploadProjectFiles } from '../../../hooks/useProjectFiles';
import { proposalProjectApiConnect } from '../../../lib/api/proposalProjectApi.connect';
import { useProcessingJobsStore } from '../../../lib/processing/jobsStore';
import { parseUtcDate } from '../_shared/api/nullable';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { DocumentPreviewDialog } from '../_shared/components/DocumentPreviewDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { ProgramDocumentPicker } from '../_shared/components/ProgramDocumentPicker';
import { ATTACHMENT_DOCUMENT_CATEGORY, CONTRACT_DOCUMENT_CATEGORY, LET_SPECRA_PROPOSE_TEAM, PROGRAM_DOCUMENT_CATEGORY_OPTIONS, SOW_DOCUMENT_CATEGORY, SOW_DOCUMENT_CATEGORY_HELP } from '../_shared/constants/documentCategories';
import { useWorkstreams } from '../Deliverables/hooks';
import { useProgram } from '../Programs/hooks';
import { useProgramWriteAccess } from '../Programs/programWriteAccess';
import { DocumentsList, TagPicker } from './DocumentsList';
import { useCreateProgramDocument, useDeleteProgramDocument, useProgramDocuments, useSetProgramDocumentTags } from './hooks';
import { SOW_INTAKE_STARTED_TOAST, startSowIntake } from './useUploadSowDocument';
const fields = [
    {
        kind: 'text',
        name: 'title',
        label: 'Title',
        required: true,
        maxLength: 300
    },
    {
        kind: 'textarea',
        name: 'description',
        label: 'Description',
        rows: 2
    },
    {
        kind: 'date',
        name: 'docDate',
        label: 'Document date',
        required: true
    },
    {
        kind: 'select',
        name: 'category',
        label: 'Type',
        required: true,
        options: PROGRAM_DOCUMENT_CATEGORY_OPTIONS,
        optionHelp: [
            {
                label: SOW_DOCUMENT_CATEGORY,
                text: SOW_DOCUMENT_CATEGORY_HELP
            }
        ],
        optionHelpTestId: Testids.deliveryDocs.documentCategoryHelp
    }
];
/** When the row's last processing attempt was recorded (0 when never). */ function attemptedAt(row) {
    return row.processingAttemptedAt ? Number(row.processingAttemptedAt.seconds) : 0;
}
/**
 * Categories whose new upload is read into the program's contract: a Contract
 * re-extracts as the award; an attachment or addendum is stored and filed
 * (uploaded on its own it is never read); a SOW / Execution Plan runs SOW
 * intake.
 */ function isExtractionCategory(category) {
    return category === CONTRACT_DOCUMENT_CATEGORY || category === ATTACHMENT_DOCUMENT_CATEGORY || category === SOW_DOCUMENT_CATEGORY;
}
/**
 * Starts the read of a recorded Contract or attachment document through zou
 * ProcessBatch and tracks it in the processing tray. An attachment declares
 * itself one: it is stored and filed with the program's documents, however
 * its content classifies alone (SAD-850). Forcing 'base' would build a
 * contract from the addendum.
 */ async function startContractRead({ programId, zouProjectId, fileId, category }) {
    const isAttachment = category === ATTACHMENT_DOCUMENT_CATEGORY;
    const batch = await proposalProjectApiConnect.processBatch(zouProjectId, [
        fileId
    ], {
        documentKind: isAttachment ? 'attachment' : 'base'
    });
    useProcessingJobsStore.getState().registerJob({
        batchId: batch.batchId,
        zouProjectId,
        kind: 'contract-docs',
        label: isAttachment ? 'Filing attachment' : 'Re-extracting contract',
        link: `/delivery/programs/${programId}/docs`
    });
}
export function DocumentsTab({ programId }) {
    const access = useProgramWriteAccess(programId);
    // Retried rows, keyed to the failed attempt they retry: the row shows
    // Processing until reverie records the new attempt, without waiting for it.
    const [retried, setRetried] = useState(new Map());
    const isRetryPending = useCallback((row)=>row.processingStatus === 'failed' && retried.get(row.id) === attemptedAt(row), [
        retried
    ]);
    const { data, isLoading, refetch } = useProgramDocuments(programId, (items)=>items.some(isRetryPending));
    const { data: programData, isLoading: isProgramLoading } = useProgram(programId);
    const zouProjectId = programData?.program?.zouProjectId ?? '';
    const create = useCreateProgramDocument();
    const remove = useDeleteProgramDocument();
    const setTags = useSetProgramDocumentTags();
    const upload = useUploadArtifacts();
    const uploadProjectFiles = useUploadProjectFiles();
    const { data: workstreams = [] } = useWorkstreams(programId);
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [fileId, setFileId] = useState('');
    // Tags for the document being recorded; kept outside the form like the SOW team.
    const [formTags, setFormTags] = useState([]);
    // A locally staged file, uploaded only on submit — cancelling the dialog
    // orphans nothing, and a failed upload records nothing. Mutually exclusive
    // with the picker: one document references one file.
    const [stagedFiles, setStagedFiles] = useState([]);
    // Which fileId, if any, was produced by the Contract project-file upload
    // lane. stagedFiles is cleared right after upload (see below), so a retry
    // after a failed create can't tell "new contract upload" from "legacy
    // upload" by stagedFiles alone — this survives across that retry.
    const [contractLaneFileId, setContractLaneFileId] = useState(null);
    // SOW team choice (SAD-898). Default lets intake propose; a real id names
    // the program workstream. Kept outside the form so SpecraCombobox can hold
    // the propose sentinel without fighting empty-string → undefined mapping.
    const [sowTeamChoice, setSowTeamChoice] = useState(LET_SPECRA_PROPOSE_TEAM);
    // ?doc= is the open document (a program document id, or its file's
    // artifact id), so a chat link, a reload or Back reopens it. The preview is
    // derived from the URL and the loaded rows, never copied into state.
    const [searchParams, setSearchParams] = useSearchParams();
    const docParam = searchParams.get('doc') ?? '';
    const setDocParam = useCallback((id)=>setSearchParams((prev)=>{
            const params = new URLSearchParams(prev);
            if (id) params.set('doc', id);
            else params.delete('doc');
            return params;
        }, {
            replace: !id
        }), [
        setSearchParams
    ]);
    const linkedDoc = docParam ? (data?.items ?? []).find((d)=>d.id === docParam || d.fileId === docParam) : undefined;
    const previewing = linkedDoc?.fileId ? linkedDoc : null;
    const docUnavailable = !!docParam && !isLoading && !previewing;
    const programTags = useMemo(()=>[
            ...new Set((data?.items ?? []).flatMap((d)=>d.tags))
        ].sort((x, y)=>x.localeCompare(y)), [
        data
    ]);
    const processingOf = useCallback((row)=>isRetryPending(row) ? 'processing' : row.processingStatus, [
        isRetryPending
    ]);
    // Retry re-runs the read through the same ProcessBatch path as the upload:
    // a SOW's intake, or a Contract's or attachment's read.
    const handleRetry = useCallback(async (row)=>{
        setRetried((prev)=>new Map(prev).set(row.id, attemptedAt(row)));
        try {
            if (row.category === SOW_DOCUMENT_CATEGORY) {
                await startSowIntake({
                    programId,
                    zouProjectId,
                    attachmentId: row.fileId
                });
                toast.success(SOW_INTAKE_STARTED_TOAST);
            } else {
                await startContractRead({
                    programId,
                    zouProjectId,
                    fileId: row.fileId,
                    category: row.category
                });
                toast.success('Reading the document again — watch the processing tray.');
            }
            void refetch();
        } catch (err) {
            setRetried((prev)=>{
                const next = new Map(prev);
                next.delete(row.id);
                return next;
            });
            toast.error(`Retry failed to start: ${err instanceof Error ? err.message : 'unknown error'}`);
        }
    }, [
        programId,
        zouProjectId,
        refetch
    ]);
    const canRetry = access.recordDocuments && !!zouProjectId;
    const sowTeamOptions = useMemo(()=>[
            {
                value: LET_SPECRA_PROPOSE_TEAM,
                label: 'Let Specra propose the team'
            },
            ...workstreams.map((team)=>({
                    value: team.id,
                    label: team.name,
                    description: team.aliases.length ? `Also called: ${team.aliases.join(', ')}` : undefined,
                    keywords: team.aliases
                }))
        ], [
        workstreams
    ]);
    const handleSubmit = async (values)=>{
        // A Contract submit while the program lookup is still pending must not
        // silently read an empty zouProjectId as "no linked project" — that's
        // indistinguishable from the real fallback case below and would drop the
        // upload into the legacy record-only path nondeterministically. Block
        // and let the user retry once the lookup resolves.
        if (isExtractionCategory(values.category) && isProgramLoading) {
            throw new Error('Program details are still loading — wait a moment and try again to upload this document.');
        }
        // Route into the converging re-extraction lane (zou ProcessBatch ->
        // contract pipeline -> ryugu SyncFromExtraction -> pangaea
        // stageReExtractionChanges) only for a genuinely NEW upload of a
        // Contract-category document against a program backed by a zou contract
        // project. A document picked from ProgramDocumentPicker is already
        // recorded — there's no new file to feed the pipeline, so picking always
        // takes the legacy record-only path below regardless of category. A
        // Contract-category upload with no zouProjectId (a hand-created program
        // with no contract project behind it) also falls back to the legacy
        // path: there is nothing to extract against, and the user must not be
        // blocked from recording the document.
        const isNewContractUpload = isExtractionCategory(values.category) && !!zouProjectId && stagedFiles.length > 0;
        let resolvedFileId = fileId;
        if (stagedFiles.length > 0) {
            if (isNewContractUpload) {
                const result = await uploadProjectFiles.mutateAsync({
                    projectId: zouProjectId,
                    files: stagedFiles
                });
                if (result.failed.length > 0) {
                    throw new Error(result.failed.map((f)=>`${f.name}: ${f.error}`).join('; '));
                }
                // attachArtifactsToProject creates the project Attachment keyed by the
                // artifact id itself (tesoro mode: attachment_id == artifact_id), so
                // the same id returned here doubles as the document's fileId below.
                resolvedFileId = result.files[0]?.attachmentId ?? '';
            } else {
                const result = await upload.mutateAsync({
                    files: stagedFiles
                });
                if (result.failed.length > 0) {
                    throw new Error(result.failed.map((f)=>`${f.name}: ${f.error}`).join('; '));
                }
                resolvedFileId = result.files[0]?.artifactId ?? '';
            }
            if (!resolvedFileId) {
                throw new Error('Upload did not return a file id — the document was not recorded');
            }
            // The artifact is uploaded now. Move it out of staging so a retry after
            // a failed create reuses it instead of uploading a second copy.
            setStagedFiles([]);
            setFileId(resolvedFileId);
            // Remember which lane produced resolvedFileId beyond this call — if
            // create fails below, the retry's stagedFiles will already be empty
            // and can no longer tell the lane apart on its own.
            setContractLaneFileId(isNewContractUpload ? resolvedFileId : null);
        }
        if (!resolvedFileId) {
            throw new Error('Pick a file or upload one before recording the document');
        }
        const isContractReExtraction = isExtractionCategory(values.category) && !!zouProjectId && (isNewContractUpload || contractLaneFileId === resolvedFileId);
        await create.mutateAsync({
            programId,
            title: values.title,
            description: values.description ?? '',
            // doc_date is date-only: the column holds a calendar day, and every
            // other writer (reverie's contract sync) anchors it at UTC midnight.
            // Parsing the picked day as LOCAL midnight would store the previous
            // day's instant for any user east of UTC (Tokyo: 2026-05-13 ->
            // 2026-05-12T15:00Z), and the UTC read above would then render the day
            // before the one that was typed. parseUtcDate keeps write and read on
            // the same anchor so the day round-trips in every zone. The shared
            // local parser is left alone — other forms hold true local instants.
            docDate: parseUtcDate(values.docDate),
            fileId: resolvedFileId,
            // The server resolves the display filename from the artifact itself
            // and ignores this field — see ryugu's CreateProgramDocument.
            fileName: '',
            category: values.category,
            // The server derives this from the authenticated caller and ignores
            // this field — see ryugu's CreateProgramDocument.
            uploadedBy: '',
            tags: formTags
        });
        setFormOpen(false);
        setFormTags([]);
        setFileId('');
        setStagedFiles([]);
        setContractLaneFileId(null);
        setSowTeamChoice(LET_SPECRA_PROPOSE_TEAM);
        if (isContractReExtraction) {
            // The document is already recorded above, so a processBatch failure here
            // must not roll it back or read as an overall failure — surface the
            // extraction failure distinctly so the user knows the file landed but
            // re-extraction did not start.
            // An attachment uploaded on its own is never read with the award or a
            // modification; only one uploaded beside them is.
            // A SOW / Execution Plan declares document_kind=sow so intake runs
            // (SAD-898); review items land on ContractIntake → Modifications.
            const isAttachment = values.category === ATTACHMENT_DOCUMENT_CATEGORY;
            const isSow = values.category === SOW_DOCUMENT_CATEGORY;
            const workstreamId = isSow && sowTeamChoice !== LET_SPECRA_PROPOSE_TEAM ? sowTeamChoice : undefined;
            try {
                if (isSow) {
                    await startSowIntake({
                        programId,
                        zouProjectId,
                        attachmentId: resolvedFileId,
                        workstreamId
                    });
                    toast.success(SOW_INTAKE_STARTED_TOAST);
                } else {
                    await startContractRead({
                        programId,
                        zouProjectId,
                        fileId: resolvedFileId,
                        category: values.category
                    });
                    toast.success(isAttachment ? "Attachment filed with the program's documents. It is not read on its own: upload it with its modification to have it read as part of that modification." : 'Contract recorded — re-extracting now (watch the processing tray). Proposed changes will appear on the Modifications tab shortly.');
                }
            } catch (err) {
                const detail = err instanceof Error ? err.message : 'unknown error';
                toast.error(isSow ? `Document recorded, but SOW intake failed to start: ${detail}` : `Document recorded, but re-extraction failed to start: ${detail}`);
            }
        }
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>{
                        setFileId('');
                        setFormTags([]);
                        setStagedFiles([]);
                        setContractLaneFileId(null);
                        setSowTeamChoice(LET_SPECRA_PROPOSE_TEAM);
                        setFormOpen(true);
                    },
                    disabled: !access.recordDocuments,
                    "data-testid": Testids.deliveryDocs.documentCreate,
                    children: "Record Document"
                })
            }),
            docUnavailable && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "framed",
                title: "This document isn't available.",
                onDismiss: ()=>setDocParam(''),
                dismissLabel: "Dismiss",
                "data-testid": Testids.deliveryDocs.documentUnavailable,
                children: "It is not on this program's Docs, or it has no file to open."
            }),
            /*#__PURE__*/ _jsx(DocumentsList, {
                documents: data?.items ?? [],
                isLoading: isLoading,
                canEdit: access.recordDocuments,
                canDelete: access.deleteDocuments,
                onOpen: (row)=>setDocParam(row.id),
                processingOf: processingOf,
                onRetry: canRetry ? (row)=>isExtractionCategory(row.category) ? ()=>void handleRetry(row) : undefined : undefined,
                onSetTags: (row, tags)=>setTags.mutate({
                        id: row.id,
                        tags
                    }),
                onDelete: setDeleting
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Record Program Document",
                fields: fields,
                defaultValues: {
                    title: '',
                    description: '',
                    docDate: '',
                    category: 'Other'
                },
                extraContent: (form)=>/*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            form.watch('category') === SOW_DOCUMENT_CATEGORY && /*#__PURE__*/ _jsx("div", {
                                "data-testid": Testids.deliveryDocs.sowTeamPicker,
                                children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                                    label: "Team",
                                    placeholder: "Let Specra propose the team",
                                    searchPlaceholder: "Search teams…",
                                    value: sowTeamChoice,
                                    onValueChange: (value)=>setSowTeamChoice(value ?? LET_SPECRA_PROPOSE_TEAM),
                                    options: sowTeamOptions
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Tags",
                                htmlFor: "documentTags",
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-center gap-1.5",
                                    "data-testid": Testids.deliveryDocs.documentFormTags,
                                    children: [
                                        formTags.map((tag)=>/*#__PURE__*/ _jsx(SpecraTag, {
                                                onRemove: ()=>setFormTags((prev)=>prev.filter((t)=>t !== tag)),
                                                removeLabel: `Remove tag ${tag}`,
                                                children: tag
                                            }, tag)),
                                        /*#__PURE__*/ _jsx(TagPicker, {
                                            label: "Add tag",
                                            suggestions: programTags.filter((t)=>!formTags.some((x)=>x.toLowerCase() === t.toLowerCase())),
                                            canCreate: true,
                                            onAdd: (tag)=>setFormTags((prev)=>prev.some((x)=>x.toLowerCase() === tag.toLowerCase()) ? prev : [
                                                        ...prev,
                                                        tag
                                                    ])
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(ProgramDocumentPicker, {
                                id: "documentFileId",
                                label: "File",
                                helpText: "Pick a file from the documents already recorded on this program.",
                                value: fileId,
                                onChange: (id)=>{
                                    setFileId(id);
                                    if (id) setStagedFiles([]);
                                },
                                programId: programId
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "…or upload a new file:"
                            }),
                            /*#__PURE__*/ _jsx(FileUploader, {
                                files: stagedFiles,
                                onChange: (f)=>{
                                    const one = f.slice(-1);
                                    setStagedFiles(one);
                                    if (one.length > 0) setFileId('');
                                },
                                disabled: create.isPending || upload.isPending || uploadProjectFiles.isPending,
                                inputTestid: Testids.deliveryDocs.documentUploadInput
                            })
                        ]
                    }),
                onSubmit: handleSubmit,
                isSubmitting: create.isPending || upload.isPending || uploadProjectFiles.isPending,
                submitLabel: "Record"
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Document",
                description: deleting ? `Delete "${deleting.title}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            previewing && /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: previewing.fileId,
                title: previewing.title,
                open: !!previewing,
                onOpenChange: (open)=>!open && setDocParam('')
            })
        ]
    });
}
